import { Box, Button, createStyles, Flex, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { CreateSignatureModal } from "./CreateSignatureModal";
import { useSelector } from "react-redux";
import { useEffect } from "react";
import {
  changeSignatureNotification,
  getSignatureById,
} from "../../../../services/signature";
import { decrypt, showError, showSuccess } from "../../../../utils";

const useStyles = createStyles((theme) => ({
  content: {
    background: "#fff",
    borderRadius: "16px",
    padding: "65px 44px",
    marginBlock: 30,
  },
  createBtn: {
    padding: "12px 20px",
    borderRadius: "8px",
    border: "1px solid #FFAF4A",
    backgroundColor: "#FFAF4A",
    height: 44,
    boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    fontSize: 16,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "#262626",
    ":hover": {
      backgroundColor: "#FFAF4A",
      opacity: 0.8,
    },
  },
  emptyReportsBox: {
    minHeight: 442,
    padding: 41,
    marginInline: "auto",
    marginBottom: 68,
    marginTop: 85,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    borderRadius: 20,
    border: "1px solid #E5E5E5",
    background: "#FFF",
    maxWidth: 761,
    width: "100%",
  },
  iconBox: {
    height: 158,
    width: 158,
    borderRadius: "50%",
    borderTop: "1px solid #D4D4D4",
    background:
      "linear-gradient(180deg, #F1F1F1 0%, rgba(255, 255, 255, 0.00) 81.19%)",
    "& img": {
      height: 64,
      width: 64,
    },
  },

  signatureLabelAndBox: {
    "& > .label": {
      fontSize: 14,
      fontWeight: 400,
      fontFamily: "Inter",
      color: "#171717",
      marginBottom: 5,
    },
    "& > .signViewBox": {
      objectFit: "contain",
      maxWidth: "200px",
      borderRadius: 10,
      border: "0.5px solid #979797",
      height: "auto",
    },
  },
}));

const MySignature = () => {
  const { classes } = useStyles();
  const [opened, { open, close }] = useDisclosure(false);
  const { profile } = useSelector((state) => state?.profile);
  const { signature, signatureLoading } = useSelector(
    (state) => state?.signature
  );
  const isStaff = profile?.user?.type === "staff";

  useEffect(() => {
    getSignatureById();
  }, []);

  const handleChangeSignature = async (id) => {
    const response = await changeSignatureNotification({
      signatureId: signature?._id,
      userId: signature?.staff,
    });
    if (response?.status === 200) {
      showSuccess(response?.data?.message);
    } else {
      showError(response?.data?.message);
    }
  };

  return (
    <Box className={classes.content}>
      <Flex direction="column" gap={60} align="center" mb={100}>
        <Text fz={24} ff="Inter" color="#171717" fw={600}>
          Manage Signature
        </Text>
        <Button
          className={classes.createBtn}
          onClick={open}
          disabled={signature?.signatureActive}
        >
          Create Signature
        </Button>
      </Flex>
      <Box sx={{ borderBottom: "1px solid #D4D4D4" }}>
        <Text fz={20} ff="Inter" color="#171717" fw={600}>
          Your Signature
        </Text>
        <Text fz={14} ff="Inter" color="#737373" fw={400} p={4} mt={5} mb={10}>
          View and edit your signature here
        </Text>
      </Box>
      {signature ? (
        <Box pt={34}>
          <Flex pt={10} pb={17} align="center" gap={50}>
            <Text fz={18} fw={600} ff="Inter" color="#1E1E1E">
              {profile?.user?.name || ""}
            </Text>
            <Flex sx={{ flex: 1 }} gap={50} align="center">
              <Box className={classes.signatureLabelAndBox}>
                <Text className="label">Signature</Text>
                {signature?.signature ? (
                  <img
                    className="signViewBox"
                    src={decrypt(signature?.signature) || ""}
                    width={300}
                    alt="signature"
                  />
                ) : signature?.signatureImage ? (
                  <img
                    src={decrypt(signature?.signatureImage) || ""}
                    width={200}
                    alt="signature"
                  />
                ) : (
                  <Flex
                    align="center"
                    justify="center"
                    className="signViewBox"
                    w={300}
                    fz={28}
                    fw={400}
                    ff="Island Moments"
                    color="#171717"
                  >
                    {signature?.signatureText || ""}
                  </Flex>
                )}
              </Box>
              <Box className={classes.signatureLabelAndBox}>
                <Text className="label">Initials</Text>
                {signature?.signatureInitials ? (
                  <img
                    className="signViewBox"
                    src={decrypt(signature?.signatureInitials) || ""}
                    width={200}
                    alt="initials"
                  />
                ) : signature?.signatureInitialsText ? (
                  <Flex
                    align="center"
                    justify="center"
                    className="signViewBox"
                    miw={200}
                    fz={28}
                    fw={400}
                    ff="Island Moments"
                    color="#171717"
                  >
                    {signature?.signatureInitialsText}
                  </Flex>
                ) : null} 
              </Box>
            </Flex>
            {(isStaff && signature?.signChangeRequest) || !isStaff ? (
              <Button
                className={classes.createBtn}
                onClick={open}
                loading={signatureLoading}
                disabled={signatureLoading}
              >
                Update Signature
              </Button>
            ) : null}
            {isStaff && (
              <>
                <Flex direction={"column"} justify={"start"} gap={10}>
                  <Button
                    className={classes.createBtn}
                    onClick={() => handleChangeSignature()}
                    loading={signatureLoading}
                    disabled={signatureLoading}
                  >
                    Request Change
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        </Box>
      ) : (
        <Box className={classes.emptyReportsBox}>
          <Flex align="center" justify="center" className={classes.iconBox}>
            <img src="/assets/images/signaturePadIcon.png" alt="" />
          </Flex>
          <Text fz={20} fw={600} ff="Inter" color="#171717">
            Create Signature
          </Text>
          <Text mt={16} fz={16} fw={400} ff="Inter" color="#737373">
            Please click ‘Create Signature’ to start adding a new signature!
          </Text>
        </Box>
      )}
      <CreateSignatureModal opened={opened} onClose={close} />
    </Box>
  );
};
export default MySignature;
